import { edgeId, faceNormal, findFace } from './edges.js';
import { cross3, dot3, normalize3, sub3 } from './vector-math.js';
import { FACE_NOT_RENDERABLE } from './validation.js';

// Tight on purpose: these exist to recognize triangles that are genuinely
// the same flat surface (a box's two half-quad triangles, textbook-coplanar),
// not to approximate "roughly flat". A looser epsilon risks silently pulling
// in a neighbor that only looks flat-ish, merging surfaces a caller didn't
// intend to extrude together.
export const REGION_NORMAL_EPSILON = 1e-6;
export const REGION_PLANE_EPSILON = 1e-6;

/**
 * Discover the connected coplanar region containing `faceId`: a breadth-first
 * walk over triangles that share an edge with a face already in the region,
 * where the neighbor also (a) has a normal parallel to the seed face's normal
 * within `normalEpsilon`, and (b) has every vertex within `planeEpsilon` of
 * the seed face's own plane. Both conditions matter: normal-only would treat
 * two triangles as coplanar even if one were offset along that normal (e.g.
 * a stair-step), and connectivity-only would never reach a face that isn't
 * actually mesh-adjacent — so two separate, unconnected coplanar patches
 * elsewhere in the mesh (say, opposite ends of a flat wall with a gap
 * between them) are never merged, only a face's own contiguous surface is.
 *
 * A neighbor that turns out to be degenerate (zero-area) is skipped, not
 * treated as an error — the region simply doesn't extend across it; only the
 * seed face's own degeneracy is a hard failure (via `faceNormal` below).
 *
 * Returns face ids in the topology's own existing array order, the same
 * deterministic order `extrudeFaceSet` uses to build the region's vertex
 * order and cap.
 */
export function coplanarRegion(topology, faceId, options = {}) {
  const normalEpsilon = options.normalEpsilon ?? REGION_NORMAL_EPSILON;
  const planeEpsilon = options.planeEpsilon ?? REGION_PLANE_EPSILON;

  const seed = findFace(topology, faceId);
  const referenceNormal = faceNormal(topology, faceId);
  const positionById = new Map(topology.vertices.map((v) => [v.id, v.position]));
  const planePoint = positionById.get(seed.vertexIds[0]);

  const facesByEdge = new Map();
  for (const face of topology.faces) {
    const ids = face.vertexIds;
    for (let i = 0; i < 3; i++) {
      const id = edgeId(ids[i], ids[(i + 1) % 3]);
      const list = facesByEdge.get(id);
      if (list) list.push(face.id);
      else facesByEdge.set(id, [face.id]);
    }
  }
  const faceById = new Map(topology.faces.map((f) => [f.id, f]));

  const regionIds = new Set([seed.id]);
  const queue = [seed.id];
  while (queue.length > 0) {
    const currentId = queue.shift();
    const current = faceById.get(currentId);
    const ids = current.vertexIds;
    for (let i = 0; i < 3; i++) {
      const eId = edgeId(ids[i], ids[(i + 1) % 3]);
      for (const neighborId of facesByEdge.get(eId) ?? []) {
        if (regionIds.has(neighborId)) continue;
        const neighbor = faceById.get(neighborId);
        let neighborNormal;
        try {
          neighborNormal = faceNormal(topology, neighborId);
        } catch (error) {
          // Only a neighbor with no usable normal is skipped — silently
          // swallowing every faceNormal failure here would also hide a
          // structurally corrupt face (one referencing an unknown vertex id),
          // which is a real bug elsewhere in the topology, not a legitimate
          // "doesn't qualify for this region" case. Keyed on the error's own
          // marker rather than its wording, so both unusable-normal reasons
          // (zero area, non-finite extent) are covered without the skip
          // quietly widening the next time a message is reworded.
          if (error.code === FACE_NOT_RENDERABLE) continue;
          throw error;
        }
        if (dot3(referenceNormal, neighborNormal) < 1 - normalEpsilon) continue;
        const coplanar = neighbor.vertexIds.every((vertexId) => {
          const distance = dot3(sub3(positionById.get(vertexId), planePoint), referenceNormal);
          return Math.abs(distance) < planeEpsilon;
        });
        if (!coplanar) continue;
        regionIds.add(neighborId);
        queue.push(neighborId);
      }
    }
  }

  return topology.faces.filter((f) => regionIds.has(f.id)).map((f) => f.id);
}

/**
 * The inward unit normal, *within the region's own plane*, of the directed
 * boundary edge (a -> b) — i.e. which way is "into" the polygon at that
 * edge. For a boundary loop wound consistently with `referenceNormal` (as
 * `boundaryLoopOfFaceSet` always produces), rotating the edge direction by
 * +90° about `referenceNormal` (`cross(referenceNormal, edgeDir)`) points
 * left of travel, which is the interior side. Returns null for a
 * zero-length edge (a repeated boundary vertex — malformed input).
 */
export function inwardEdgeNormal(referenceNormal, aPos, bPos) {
  const edgeDir = normalize3(sub3(bPos, aPos));
  if (!edgeDir) return null;
  return normalize3(cross3(referenceNormal, edgeDir));
}
